import classnames from 'classnames';

import type {FilterObject} from 'nutshell-core/types';

import {ButtonGroup} from 'shells/button-group/button-group';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {StatusBarSecondaryTimestamp} from 'shells/control-bar/status-bar-secondary-timestamp';
import {EmptyStateSmall} from 'shells/empty-states/empty-state-small';
import {ErrorState} from 'shells/error-state/error-state';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ClipboardChecklistIcon} from 'shells/icon/svg/clipboard-checklist-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReportsIcon} from 'shells/icon/svg/reports-icon';
import {WebFormIcon} from 'shells/icon/svg/web-form-icon';
import {Illustration} from 'shells/illustrations/illustration';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {
    FormPublishInput,
    FormUpdateInput,
    MultiPageFormFragment,
} from 'nutshell-graphql-types';

import {PageContainer, PageTitleBar} from '../../../layout';
import type {ReportURLParams} from '../../../reports/report-types';

import {
    MAIN_TAB_ID,
    ENGAGEMENT_TAB_ID,
    SUBMISSIONS_TAB_ID,
    RESPONDENTS_TAB_ID,
} from '../../constants';
import {getDefaultFieldsForSubmissionsTable} from '../../helpers';

import {FormStatusPrimaryText} from '../form-status-primary-text';
import {FormRespondents} from '../respondents/form-respondents';

import {fieldsFromPublishedVersionPages} from './helpers';
import {MultiPageEmbedFormButton} from './multi-page-embed-form-button';
import {MultiPageFormEngagement} from './multi-page-form-engagement';
import {MultiPageFormMainTab} from './multi-page-form-main-tab';
import {MultiPageFormSubmissions} from './submissions/multi-page-form-submissions';

interface Props {
    form: MultiPageFormFragment | null | undefined;
    error?: string | null | undefined;
    isLoading: boolean;
    activeTab: string;
    onSetSelectedTab: (newTab: string) => void;
    onPublishForm: (arg1: FormPublishInput) => Promise<any>;
    onUpdateForm: (arg1: FormUpdateInput) => Promise<any>;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
    refetchForm: () => Promise<any>;
}

export function MultiPageFormPageContent(props: Props) {
    const formFields =
        props.form && props.form.publishedVersion
            ? fieldsFromPublishedVersionPages(props.form.publishedVersion).map((field) => ({
                  type: field.type,
                  fieldName: field.fieldName,
                  entityName: field.entityName,
                  attribute: field.attribute,
              }))
            : undefined;

    const defaultFields = formFields
        ? getDefaultFieldsForSubmissionsTable(formFields).split(',')
        : getDefaultFieldsForSubmissionsTable().split(',');

    const tabOptions = [
        {
            id: MAIN_TAB_ID,
            label: 'Form',
            icon: WebFormIcon,
            href: '#',
            component: props.form ? <MultiPageFormMainTab form={props.form} /> : <></>,
        },
        {
            id: ENGAGEMENT_TAB_ID,
            label: 'Engagement',
            icon: ReportsIcon,
            href: '#',
            component: props.form ? (
                <MultiPageFormEngagement
                    form={props.form}
                    onParamChange={props.onParamChange}
                    onParamPush={props.onParamPush}
                    urlParams={props.urlParams}
                />
            ) : (
                <></>
            ),
        },
        {
            id: SUBMISSIONS_TAB_ID,
            label: 'Submissions',
            icon: ClipboardChecklistIcon,
            href: '#',
            component: props.form ? (
                <MultiPageFormSubmissions
                    formId={props.form.id}
                    urlParams={props.urlParams}
                    onParamChange={props.onParamChange}
                />
            ) : (
                <></>
            ),
        },
        {
            id: RESPONDENTS_TAB_ID,
            label: 'Respondents',
            icon: AudienceIcon,
            href: '#',
            component: props.form ? (
                <FormRespondents
                    formId={props.form.id}
                    defaultFields={defaultFields}
                    numSubmissions={props.form.numSubmissions}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    customEmptyStateComponent={
                        <EmptyStateSmall
                            title='It looks like this form didn’t have any submissions'
                            subtitle='When your form has one or more submissions, they’ll show up here'
                            icon={
                                <div style={{width: '75px'}}>
                                    <Illustration src='magnifying-glass-empty-state' width='100%' />
                                </div>
                            }
                        />
                    }
                />
            ) : (
                <></>
            ),
        },
    ];

    const className = classnames('full-height full-width ', {
        'overflow-hidden': props.activeTab === SUBMISSIONS_TAB_ID,
        'overflow-auto': props.activeTab !== SUBMISSIONS_TAB_ID,
    });

    return (
        <PageContainer
            title={props.form ? props.form.name : 'Form'}
            noPadding={props.activeTab === SUBMISSIONS_TAB_ID}
            whiteBackground={props.activeTab === MAIN_TAB_ID}
            pageTitleBar={
                <PageTitleBar
                    title={props.form ? props.form.name : 'Form'}
                    subheaderPrimaryText='Edit your form'
                    noMaxWidth={true}
                    breadcrumbs={[
                        {
                            to: '/email-marketing/forms',
                            text: 'Forms',
                        },
                    ]}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) =>
                            props.form
                                ? props.onUpdateForm({
                                      form: {
                                          id: props.form.id,
                                          name: newValue,
                                      },
                                  })
                                : undefined,
                        placeholder: 'Name this form',
                    }}
                    buttons={
                        <ButtonGroup>
                            <Link
                                // @ts-expect-error
                                disabled={props.isLoading || !props.form}
                                variant='button-secondary'
                                size='big'
                                newTab={true}
                                href={props.form ? props.form.sharableViewInBrowserLink : undefined}
                            >
                                View form
                            </Link>
                            <MultiPageEmbedFormButton form={props.form} />
                        </ButtonGroup>
                    }
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={
                        props.form
                            ? {
                                  primaryText: (
                                      <FormStatusPrimaryText
                                          numSubmissions={props.form.numSubmissions}
                                          activeTab={props.activeTab}
                                          activeFilters={props.activeFilters}
                                      />
                                  ),

                                  secondaryText: (
                                      <StatusBarSecondaryTimestamp
                                          datetime={props.form.lastSubmittedTime}
                                          prefix='Last submitted'
                                          emptyText='This form hasn’t been submitted yet'
                                      />
                                  ),
                              }
                            : undefined
                    }
                    tabs={{
                        selectedTab: props.activeTab || MAIN_TAB_ID,
                        options: tabOptions,
                        onChangeTab: (newTab) => {
                            props.onSetSelectedTab(newTab);
                            props.refetchForm();
                        },
                    }}
                />
            }
        >
            <div className='routed-report-content'>
                {props.error ? <ErrorState /> : undefined}
                {!props.error && props.isLoading ? <LoadingPage padding={0} /> : undefined}
                {props.form ? (
                    <div className={className}>
                        {/* @ts-expect-error */}
                        <TabbedContent selectedTab={props.activeTab} tabOptions={tabOptions} />
                    </div>
                ) : undefined}
            </div>
        </PageContainer>
    );
}
